(() => {
  'use strict';

  const currency = String(
    window.Shopify?.currency?.active || document.documentElement.dataset.sfCurrency || ''
  ).toUpperCase();

  if (currency !== 'OMR') return;

  const arabic = document.documentElement.lang.toLowerCase().startsWith('ar');
  const priceSelector = [
    '.money',
    '.price',
    '.price-item',
    '[class*="price"]',
    '[data-slot-amount]',
    '[data-bundle-subtotal]',
    '[data-sf-product-price]'
  ].join(',');
  const excludedTags = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA']);

  function normalizeNumberToken(token) {
    const compact = String(token).replace(/\u00a0/g, '');
    const lastDot = compact.lastIndexOf('.');
    const lastComma = compact.lastIndexOf(',');
    const decimalIndex = Math.max(lastDot, lastComma);

    if (decimalIndex < 0) return token;

    const integer = compact.slice(0, decimalIndex).replace(/[.,]/g, '');
    const fraction = compact.slice(decimalIndex + 1);
    if (!/^\d+$/.test(integer) || !/^\d{1,4}$/.test(fraction)) return token;

    const trimmedFraction = fraction.replace(/0+$/, '');
    return trimmedFraction ? `${integer}.${trimmedFraction}` : integer;
  }

  function normalizeMoney(value) {
    let result = String(value).replace(
      /\d(?:[\d.,\u00a0]*\d)?/g,
      normalizeNumberToken
    );

    if (arabic) {
      result = result.replace(/\bOMR\s*([0-9][0-9.,]*)/gi, '$1 ر.ع.');
      result = result.replace(/\bOMR\b/gi, 'ر.ع.');
    }
    return result;
  }

  function isPriceContext(element) {
    return Boolean(element?.matches?.(priceSelector) || element?.closest?.(priceSelector));
  }

  function hasCurrencyMarker(value) {
    return /\bOMR\b|ر\.\s*ع\.?/i.test(String(value));
  }

  function normalizeAttribute(element, name) {
    if (!element.hasAttribute(name)) return;
    const current = element.getAttribute(name);
    if (!hasCurrencyMarker(current) && !isPriceContext(element)) return;
    const next = normalizeMoney(current);
    if (next !== current) element.setAttribute(name, next);
  }

  function normalizeElement(element) {
    if (!(element instanceof Element) || excludedTags.has(element.tagName)) return;

    if (element.hasAttribute('data-money-format')) {
      const current = element.getAttribute('data-money-format');
      const next = current.replace(
        /{{\s*amount_with_comma_separator\s*}}0(?=\s*OMR\b)/gi,
        '{{ amount }}'
      );
      if (next !== current) element.setAttribute('data-money-format', next);
    }

    [
      'aria-label',
      'title',
      'data-text',
      'data-sf-product-price',
      'value',
      'data-value',
      'min',
      'max'
    ].forEach((name) => normalizeAttribute(element, name));
  }

  function normalizeTextNode(node) {
    const parent = node.parentElement;
    if (!parent || excludedTags.has(parent.tagName)) return;
    const current = node.nodeValue;
    if (!hasCurrencyMarker(current) && !isPriceContext(parent)) return;
    const next = normalizeMoney(current);
    if (next !== current) node.nodeValue = next;
  }

  function normalizeSubtree(root) {
    if (!root) return;
    if (root.nodeType === Node.TEXT_NODE) {
      normalizeTextNode(root);
      return;
    }
    if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) return;

    normalizeElement(root);
    root
      .querySelectorAll(
        '[data-money-format], [aria-label], [title], [data-text], [data-sf-product-price], [value], [data-value], [min], [max]'
      )
      .forEach(normalizeElement);

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) normalizeTextNode(node);
  }

  function start() {
    const root = document.body || document.documentElement;
    normalizeSubtree(root);

    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData') {
          normalizeTextNode(mutation.target);
          return;
        }
        if (mutation.type === 'attributes') {
          normalizeElement(mutation.target);
          return;
        }
        mutation.addedNodes.forEach(normalizeSubtree);
      });
    }).observe(root, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: [
        'data-money-format',
        'aria-label',
        'title',
        'data-text',
        'data-sf-product-price',
        'value',
        'data-value',
        'min',
        'max'
      ]
    });
  }

  start();
})();
